import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { type OrgAssetResponse } from '@goldcast/api/content';
import OverlayUploadSection from './OverlayUploadSection';
import Button from '@/components/atoms/Button/Button';
import { useImageOverlay } from '@/Pages/Clip/ClipPlayer/ClipAssets/ImageOverlay/useImageOverlay';
import { useMediaPickerState } from '@/Pages/BrandKit/components/MediaCard/useMediaPickerState';
import { MediaPickerTabBar } from '@/Pages/BrandKit/components/MediaCard';
import BrandKitSelector from '@/Pages/BrandKit/components/BrandKitSelector/BrandKitSelector';
import MediaEmptyState from '@/Pages/BrandKit/components/MediaCard/MediaEmptyState';
import { PlatformBrandKitImage } from '@/Pages/BrandKit/types';
import { timelineActionsStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/timelineActions';
import { classnames } from '@/libs/utils';
import { BRANDKIT_IMAGE_TABS, type BrandKitImageTab } from '@/Pages/BrandKit/constants';
import { useRemoteApp } from '@/context/RemoteAppContext/RemoteAppContext';

interface ImageTabContentProps {
  closePopup: () => void;
  recentAssets: OrgAssetResponse[];
}

const ImageTabContent = ({ closePopup, recentAssets }: ImageTabContentProps) => {
  const { isAddingImage } = useSyncExternalStore(timelineActionsStore.subscribe, timelineActionsStore.getSnapshot);
  const { uploadImage, acceptedFormats, addBrandKitImage } = useImageOverlay({ onUploadComplete: closePopup });
  const { activeBrandKitId, setActiveBrandKitId, brandKits, activeKit } = useMediaPickerState();
  const { isRemoteApp } = useRemoteApp();

  const [brandKitImageTab, setBrandKitImageTab] = useState<BrandKitImageTab>('logo');

  const brandkitTabImages = useMemo(
    () =>
      brandKitImageTab === 'logo'
        ? ((activeKit?.logos as PlatformBrandKitImage[]) ?? [])
        : ((activeKit?.photography_elements as PlatformBrandKitImage[]) ?? []),
    [brandKitImageTab, activeKit]
  );

  const handleRecentImageClick = useCallback(
    async (asset: OrgAssetResponse) => {
      await addBrandKitImage(asset.source_url, asset.name);
      closePopup();
    },
    [addBrandKitImage, closePopup]
  );

  const handleBrandkitImageClick = useCallback(
    async (item: PlatformBrandKitImage) => {
      await addBrandKitImage(item.url, item.originalName);
      closePopup();
    },
    [addBrandKitImage, closePopup]
  );

  return (
    <>
      <OverlayUploadSection
        isAdding={isAddingImage}
        uploadTitle="Upload Image"
        uploadLabel="Click to browse or drag and drop an image"
        accept={acceptedFormats}
        onFilesUpload={uploadImage}
        recentAssets={recentAssets}
        onAssetClick={handleRecentImageClick}
        renderAssetContent={asset =>
          asset.source_url ? (
            <img src={asset.source_url} alt={asset.name} className="size-full object-cover object-center" />
          ) : null
        }
        testIdPrefix="image"
      />

      {!isRemoteApp && (
        <div
          data-testid="brandkit-section"
          className={classnames(
            'flex flex-col gap-3 transition-opacity',
            isAddingImage ? 'pointer-events-none opacity-50' : ''
          )}
        >
          <h3 className="text-sm font-medium text-black">Brand Kit</h3>
          {brandKits.length === 0 ? (
            <MediaEmptyState
              icon="IconPalette"
              title="No brand kit available"
              description="Create a brand kit to apply your brand's colors, fonts, assets, voice etc to your edited content."
              ctaLabel="Add Brand Kit"
              ctaHref="/brand-kit"
            />
          ) : (
            <>
              <BrandKitSelector
                selectedBrandKitId={activeBrandKitId}
                onBrandKitChange={setActiveBrandKitId}
                showThumbnail
              />
              <MediaPickerTabBar
                tabs={BRANDKIT_IMAGE_TABS}
                activeTabId={brandKitImageTab}
                onTabChange={id => setBrandKitImageTab(id as BrandKitImageTab)}
                variant="pill"
              />
              {brandkitTabImages.length === 0 ? (
                <MediaEmptyState
                  icon="IconPhoto"
                  title={`No ${brandKitImageTab === 'logo' ? 'logos' : 'photographs'} added`}
                  description="Add images to your brand kit to reuse them across assets."
                  ctaLabel="Add Images"
                  ctaHref={activeBrandKitId ? `/brand-kit/${activeBrandKitId}/edit` : '/brand-kit'}
                />
              ) : (
                <div className="grid grid-cols-3 gap-[10px]">
                  {brandkitTabImages.map(item => (
                    <Button
                      key={item.url}
                      trackingId={`brandkit-img-${item.url}`}
                      onClick={() => handleBrandkitImageClick(item)}
                      variation="none"
                      className="flex h-[120px] flex-col overflow-hidden rounded-lg border border-slate-200 !p-0 text-left transition-all hover:border-card-active-border"
                    >
                      <div className="w-full flex-1 overflow-hidden rounded-t-lg bg-slate-100">
                        {item.url && (
                          <img
                            src={item.url}
                            alt={item.originalName}
                            className="size-full object-cover object-center"
                          />
                        )}
                      </div>
                      <span className="w-full truncate rounded-b-lg px-1.5 py-1 text-xs text-gray-700">
                        {item.originalName}
                      </span>
                    </Button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ImageTabContent;
